export type EntryType = 'invitation' | 'practice' | 'professional' | 'assisted';

/**
 * A questionnaire was started, a patient was selected, or an invitation was
 * opened (invitationId is set on open, before any input).
 */
export const hasExistingSession = ({
  questionnaireId,
  patientId,
  invitationId,
}: {
  questionnaireId: string | null | undefined;
  patientId: string | null | undefined;
  invitationId: string | null | undefined;
}): boolean => Boolean(questionnaireId || patientId || invitationId);

/**
 * Practice/professional entry over an existing session, or over a hydrated
 * state from another practice/professional with nothing filled yet.
 */
export const hasGenericEntryConflict = ({
  entryType,
  slug,
  existingPracticeSlug,
  existingProfessionalSlug,
  existingSession,
}: {
  entryType: EntryType;
  slug: string | undefined;
  existingPracticeSlug: string | null | undefined;
  existingProfessionalSlug: string | null | undefined;
  existingSession: boolean;
}): boolean =>
  (entryType === 'practice' || entryType === 'professional') &&
  (existingSession ||
    (entryType === 'practice'
      ? Boolean(existingPracticeSlug && slug && slug !== existingPracticeSlug)
      : Boolean(
          existingProfessionalSlug && slug && slug !== existingProfessionalSlug,
        )));

/**
 * Conflict unless the session was started from this same invitation —
 * re-opening the SAME invitation resumes silently.
 */
export const hasInvitationConflict = ({
  fetchedInvitationId,
  existingInvitationId,
  existingSession,
}: {
  fetchedInvitationId: string;
  existingInvitationId: string | null | undefined;
  existingSession: boolean;
}): boolean => fetchedInvitationId !== existingInvitationId && existingSession;
